import {
  Column,
  DataTableSkeleton,
  FlexGrid as Grid,
  Row,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Fragment } from 'react';
import style from './package-overview.module.scss';
import { Pricing } from 'types/evaluation';
import classNames from 'classnames';
import { US_DATE_FORMAT } from 'utils/dates';
import moment from 'moment';
import { formatNumber } from 'utils/number';
import {
  SLOT_CATEGORY_LABELS,
  shownPricingSlotCategories,
} from 'utils/slot-categories';

const pricingTypes = ['targetPricing', 'maxPricing', 'mtmPricing'] as const;

interface PackageOverviewProps {
  packageOverview: {
    maxPricing: Array<Pricing>;
    targetPricing: Array<Pricing>;
    mtmPricing?: Array<Pricing>;
  };
  loading: boolean;
  transactionEffectiveDate: string | null;
  discountToDate: string | null;
  evalExpiration: string | null;
  evalDate: string | null;
}

export const PackageOverview = ({
  packageOverview,
  loading,
  transactionEffectiveDate,
  discountToDate,
  evalExpiration,
  evalDate,
}: PackageOverviewProps) => {
  if (loading) {
    return (
      <DataTableSkeleton
        data-testid="data-table-skeleton"
        rowCount={9}
        showHeader={false}
        showToolbar={false}
        columnCount={6}
      />
    );
  }

  const pricingConfig = {
    targetPricing: 'Target Pric',
    maxPricing: 'Max Pric',
    mtmPricing: 'MTM Pric',
  };

  const defaultCurrencyFormat = [2, 2, 'currency', true] as const;
  const defaultDecimalFormat = [2, 2, 'decimal', false] as const;
  // One row per category (the shared list), then the total. A Create New Well status gets a row
  // only when the run priced it (INS-13303).
  const rowConfig = [
    ...shownPricingSlotCategories(
      pricingTypes.map((pricingType) => packageOverview[pricingType])
    ).map((type) => ({
      type,
      label: SLOT_CATEGORY_LABELS[type],
    })),
    { type: 'total', label: '' },
  ];

  return (
    <div>
      <div className={style.dateOverviewContainer}>
        <Grid>
          <Row>
            <Column md={5} lg={3} sm={5}>
              Eval Date
            </Column>
            <Column md={1} lg={1} sm={1}>
              {moment(evalDate).format(US_DATE_FORMAT) ?? '--'}
            </Column>
          </Row>
          <Row>
            <Column md={5} lg={3} sm={5}>
              Transaction Effective Date
            </Column>
            <Column md={1} lg={1} sm={1}>
              {moment(transactionEffectiveDate).format(US_DATE_FORMAT) ?? '--'}
            </Column>
          </Row>
          <Row>
            <Column md={5} lg={3} sm={5}>
              Discount to Date
            </Column>
            <Column md={1} lg={1} sm={1}>
              {moment(discountToDate).format(US_DATE_FORMAT) ?? '--'}
            </Column>
          </Row>
          <Row>
            <Column md={5} lg={3} sm={5}>
              Eval expiration
            </Column>
            <Column md={1} lg={1} sm={1}>
              {moment(evalExpiration).format(US_DATE_FORMAT) ?? '--'}
            </Column>
          </Row>
        </Grid>
      </div>
      <TableContainer>
        <Table size="sm" className={style.packageOverviewTable}>
          <TableBody>
            {pricingTypes.map((pricingType) => {
              return (
                <Fragment key={pricingType}>
                  <TableRow className={style.sectionTitle}>
                    <TableCell colSpan={8}>
                      {`${pricingConfig[pricingType]}ing`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2}></TableCell>
                    <TableCell colSpan={3} className={style.sectionBroker}>
                      Excluding Commissions (Broker Pricing)
                    </TableCell>
                    <TableCell colSpan={3} className={style.sectionSim}>
                      Including Commissions (SIM Valuation)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableHeader scope="col">Type</TableHeader>
                    <TableHeader scope="col">NRA</TableHeader>
                    <TableHeader scope="col" className={style.sectionBroker}>
                      Implied Discount Rate
                    </TableHeader>
                    <TableHeader scope="col" className={style.sectionBroker}>
                      $/NRA
                    </TableHeader>
                    <TableHeader scope="col" className={style.sectionBroker}>
                      Total $ Amount
                    </TableHeader>
                    <TableHeader scope="col" className={style.sectionSim}>
                      Implied Discount Rate
                    </TableHeader>
                    <TableHeader scope="col" className={style.sectionSim}>
                      $/NRA
                    </TableHeader>
                    <TableHeader scope="col" className={style.sectionSim}>
                      Total $ Amount
                    </TableHeader>
                  </TableRow>
                  {rowConfig.map((item) => {
                    const row = packageOverview[pricingType]?.find(
                      (row) => row.type === item.type
                    );

                    return (
                      <TableRow
                        key={`${pricingType}-${item.type}`}
                        className={classNames({
                          [style.highlightTotal]: item.type === 'total',
                        })}
                      >
                        <TableCell>
                          {item.type === 'total'
                            ? `${pricingConfig[pricingType]}e`
                            : item.label}
                        </TableCell>
                        <TableCell>
                          {formatNumber(row?.nra, ...defaultDecimalFormat)}
                        </TableCell>
                        <TableCell>
                          {formatNumber(
                            row?.excludedDiscountRate,
                            ...defaultDecimalFormat
                          )}
                          {row?.excludedDiscountRate ? '%' : ''}
                        </TableCell>
                        <TableCell>
                          {formatNumber(
                            row?.excludedDollarsPerNra,
                            ...defaultCurrencyFormat
                          )}
                        </TableCell>
                        <TableCell>
                          {formatNumber(
                            row?.excludedNpv,
                            ...defaultCurrencyFormat
                          )}
                        </TableCell>
                        <TableCell>
                          {formatNumber(
                            row?.includedDiscountRate,
                            ...defaultDecimalFormat
                          )}
                          {row?.includedDiscountRate ? '%' : ''}
                        </TableCell>
                        <TableCell>
                          {formatNumber(
                            row?.includedDollarsPerNra,
                            ...defaultCurrencyFormat
                          )}
                        </TableCell>
                        <TableCell>
                          {formatNumber(
                            row?.includedNpv,
                            ...defaultCurrencyFormat
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
